<script setup lang="ts">
import { RouterView, useRouter } from "vue-router";
import {
    onErrorCaptured,
    onMounted,
    onUnmounted,
    provide,
    ref,
    watch,
} from "vue";
import { useCsrfStore } from "@/stores/csrf";
import { useUserStore } from "@/stores/user";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useNotification } from "@kyvg/vue3-notification";
import Swal from "sweetalert2";
//import LanguagePicker from "@/components/LanguagePicker.vue";
import { createPaymentReturnHandler } from "@/payment/PaymentReturnHandler";
import { useRecaptcha } from "@/recaptcha/Recaptcha";
import { tracker } from "@/analytics/Tracker";
import { errorHandler } from "@/errors/ErrorHandler";
import { useRoute } from "vue-router";
import UserMenu from "@/components/UserMenu.vue";

const route = useRoute();

watch(
    () => route.fullPath,
    () => {
        closeMenu();
    }
);
const userStore = useUserStore();
useRecaptcha();
userStore.validToken();
const { isLoggedIn, user, isAdmission } = storeToRefs(userStore);
const { t, locale } = useI18n();
const router = useRouter();
const notification = useNotification();

errorHandler.setStrings({
    unauthorized: t("labels.unauthorizedError"),
    generic: t("labels.genericError"),
});
errorHandler.setRouter(router);

const mobileUserMenuOpen = ref(false);

function openMobileUserMenu() {
    mobileUserMenuOpen.value = true;
}

function closeMobileUserMenu() {
    mobileUserMenuOpen.value = false;
}

function toggleMobileUserMenu() {
    mobileUserMenuOpen.value ? closeMobileUserMenu() : openMobileUserMenu();
}

function handleOutsideClick() {
    if (mobileUserMenuOpen.value) {
        closeMobileUserMenu();
    }
}

onMounted(() => {
    window.addEventListener("click", handleOutsideClick);
});

onUnmounted(() => {
    window.removeEventListener("click", handleOutsideClick);
});

const pageTitle = ref("UNYP.cz");
function setPageTitle(value: string) {
    pageTitle.value = value;
}

function handleScroll() {
    const header = document.getElementById("header");
    if (!header) return;

    if (window.scrollY > 0) {
        header.classList.add("scrolled");
    } else {
        header.classList.remove("scrolled");
    }
}

provide("pageTitle", { pageTitle, setPageTitle });
watch(
    pageTitle,
    (title: string): void => {
        document.title = title;
    },
    { immediate: true }
);

onMounted(() => {
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initial check

    const app = document.getElementById("app");
    if (app) {
        const csrf = app.dataset.csrf;
        const store = useCsrfStore();
        store.setToken(csrf as string);
    }
    if (localStorage.lang) {
        locale.value = localStorage.lang;
    }
    createPaymentReturnHandler().registerHandler();
});

onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
});

onErrorCaptured((err) => {
    errorHandler.handle(err);
});

watch(locale, () => (localStorage.lang = locale.value));

let isLoggingOut = false;
async function logout(): Promise<void> {
    if (isLoggingOut) {
        return;
    }
    isLoggingOut = true;
    try {
        await userStore.logout();
        tracker.trackEvent("logout", {});
    } catch (e) {
        window.Sentry?.captureException(e);
        await Swal.fire("user.logoutError", "", "error");
        isLoggingOut = false;
        return;
    }
    isLoggingOut = false;
    notification.notify({
        title: t("user.logoutSuccess"),
        type: "success",
    });
    await router.push({ name: "home" });
}

const twClasses = {
    navLinkOuter: "menu-item group relative flex flex-col lg:flex-row",
    navLink:
        "h-100 flex items-center gap-2 py-3 font-semibold no-underline group-hover:text-royalBlue-400 group-hover:underline group-[.scrolled]/header:text-link-primary-default group-[.scrolled]/header:group-hover:text-link-primary-hover lg:px-4 lg:py-0",
};

const isMenuOpen = ref(false);

function openMenu() {
    isMenuOpen.value = true;
    document.documentElement.classList.add("menu-open");
}

function closeMenu() {
    isMenuOpen.value = false;
    document.documentElement.classList.remove("menu-open");
}

function toggleMenu() {
    if (isMenuOpen.value) {
        closeMenu();
    } else {
        openMenu();
    }
}
</script>

<template>
    <div id="upper-content" class="pb-[70px] lg:pb-0">
        <notifications position="top center" />
        <div
            class="group/header sticky bottom-0 left-0 right-0 top-0 z-40 lg:sticky"
            id="header"
        >
            <div
                class="container relative h-[100%] max-w-[1440px] px-4 lg:px-8"
            >
                <div
                    class="flex h-[100%] flex-col items-stretch justify-center lg:flex-row lg:gap-4"
                >
                    <RouterLink
                        to="/"
                        id="logo"
                        class="flex items-center my-[15px] lg:my-0 lg:py-6 no-underline self-start lg:self-auto"
                    >
                        <img
                            class="h-[100%] w-[82px] lg:w-auto"
                            src="/images/logo-full.svg"
                            alt="University of New York in Prague"
                        />
                        <span
                            class="flex ml-4 h-8 items-center border-l-[1px] border-text-secondary pl-4 text-[12px] text-text-secondary font-semibold"
                            >{{ $t("menu.applyOnline") }}</span
                        >
                    </RouterLink>

                    <div
                        id="navigation-items"
                        class="scroll order-3 ml-auto hidden h-[100%] w-full flex-col overflow-x-hidden overflow-y-scroll pb-[140px] lg:order-2 lg:flex lg:h-auto lg:w-auto lg:flex-row lg:overflow-visible lg:pb-0"
                    >
                        <div :class="twClasses.navLinkOuter" v-if="isLoggedIn">
                            <RouterLink
                                :class="twClasses.navLink"
                                to="/account/my-applications"
                            >
                                {{ $t("menu.myApplications") }}
                            </RouterLink>
                        </div>
                        <div :class="twClasses.navLinkOuter">
                            <RouterLink
                                :class="twClasses.navLink"
                                to="/online-payment"
                            >
                                {{ $t("menu.payOnline") }}
                            </RouterLink>
                        </div>
                        <div :class="twClasses.navLinkOuter">
                            <a
                                href="https://www.unyp.cz/contact/"
                                :class="twClasses.navLink"
                                target="_blank"
                                >{{ $t("menu.contact") }}</a
                            >
                        </div>

                        <div
                            class="flex items-center lg:px-4"
                            v-if="!isLoggedIn"
                        >
                            <RouterLink
                                class="btn btn-secondary btn-sm gap-2 items-center hidden lg:flex"
                                to="/login"
                            >
                                <img
                                    src="/icons/ic_user.svg"
                                    class="mt-[-2px]"
                                    alt=""
                                />
                                {{ $t("menu.login") }}
                            </RouterLink>
                        </div>
                        <div :class="twClasses.navLinkOuter" v-if="isAdmission">
                            <a href="/admin" :class="twClasses.navLink">{{
                                $t("menu.admin")
                            }}</a>
                        </div>
                    </div>

                    <div
                        class="order-2 flex items-center lg:static lg:order-3 lg:gap-8"
                    >
                        <!--
                        <div class="mb-4">
                            <LanguagePicker id="language-picker" />
                        </div>
                        -->
                        <div
                            class="menu-item group relative scale-100"
                            v-if="isLoggedIn"
                        >
                            <div
                                class="btn btn-secondary btn-sm hidden gap-2 items-center lg:flex max-w-[250px] cursor-default"
                            >
                                <img
                                    src="/icons/ic_user.svg"
                                    class="mt-[-2px] shrink-0"
                                    alt=""
                                />

                                <span
                                    class="hidden lg:flex items-center min-w-0"
                                    ><span
                                        class="truncate"
                                        :title="`${user?.first_name ?? ''} ${
                                            user?.surname ?? ''
                                        }`"
                                        >{{ user?.first_name }}
                                        {{ user?.surname }}</span
                                    ><img
                                        src="/icons/ic_arrow-down.svg"
                                        class="mt-[-2px] mr-[-4px] w-4 h-4 ml-2 shrink-0"
                                        alt=""
                                /></span>
                            </div>

                            <div
                                class="absolute left-[-25px] right-[-25px] bottom-[-50px] h-[50px] hidden group-hover:block"
                            ></div>
                            <!-- desktop usermenu -->
                            <UserMenu
                                v-if="isLoggedIn"
                                :is-logged-in="isLoggedIn"
                                :user="user"
                                @logout="logout"
                                class="hidden lg:flex left-[-10000px] lg:group-hover:right-0 lg:group-hover:left-auto lg:group-hover:opacity-100 lg:group-hover:scale-y-100"
                            />
                        </div>
                        <a
                            href="https://www.unyp.cz/"
                            target="_blank"
                            class="flex gap-2 text-text-subtle hover:text-text-subtle no-underline hover:underline hidden lg:flex font-semibold"
                            >{{ $t("menu.goToUnyp") }}
                            <img src="/icons/ic_arrow-tab.svg" alt=""
                        /></a>
                        <div
                            class="lg:hidden custom-transition fixed right-[16px] top-[18px] flex items-center gap-4 group-[.scrolled]/header:top-[8px] lg:absolute lg:right-[32px] lg:top-[82px] group-[.scrolled]/header:lg:static xl:static"
                        >
                            <div
                                class="group relative lg:hidden"
                                v-if="isLoggedIn"
                            >
                                <button
                                    @click.stop="toggleMobileUserMenu"
                                    class="btn btn-secondary btn-sm flex gap-2 items-center !px-0 w-[36px] h-[36px]"
                                >
                                    <img
                                        src="/icons/ic_user.svg"
                                        class="mt-[-2px]"
                                        alt=""
                                    />
                                </button>

                                <!-- mobilni usermenu -->
                                <UserMenu
                                    v-if="isLoggedIn && mobileUserMenuOpen"
                                    :is-logged-in="isLoggedIn"
                                    :user="user"
                                    @logout="logout"
                                    class="absolute right-0 top-[48px] rounded-xl border p-4 shadow-xl"
                                />
                            </div>

                            <RouterLink
                                v-else
                                class="btn btn-secondary btn-sm flex gap-2 items-center !px-0 w-[36px] h-[36px] lg:hidden"
                                to="/login"
                            >
                                <img
                                    src="/icons/ic_user.svg"
                                    class="mt-[-2px]"
                                    alt=""
                                />
                            </RouterLink>

                            <button
                                id="button-menu-close"
                                class="hidden lg:hidden"
                                @click="closeMenu"
                            >
                                <img
                                    src="/icons/ic_close.svg"
                                    alt="ic_close"
                                    class="homepage-close-menu h-7 w-7"
                                />
                            </button>
                            <button
                                id="button-menu-open"
                                class="lg:hidden"
                                @click="openMenu"
                            >
                                <img
                                    src="/icons/ic_menu.svg"
                                    alt="ic_close"
                                    class="homepage-hamburger h-7 w-7"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <RouterView />
    </div>

    <div class="bg-light-blue content-bottom-separator"></div>
</template>
